import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';
import { PRIORITY_CONDITION_VALUES } from 'dashboard/constants/automation';

/**
 * Composable for handling macro-related functionality
 * @returns {Object} An object containing the getMacroDropdownValues function
 */
export const useMacros = () => {
  const { t } = useI18n();
  const getters = useStoreGetters();

  const labels = computed(() => getters['labels/getLabels'].value);
  const teams = computed(() => getters['teams/getTeams'].value);
  const agents = computed(() => getters['agents/getAgents'].value);
  const issues = computed(() => getters['issues/getIssues'].value);
  const vipLevels = computed(
    () => getters['vipLevels/getActiveVipLevels'].value
  );

  /**
   * Get dropdown values based on the specified type
   * @param {string} type - The type of dropdown values to retrieve
   * @returns {Array} An array of dropdown values
   */
  const getMacroDropdownValues = type => {
    switch (type) {
      case 'assign_team':
      case 'send_email_to_team':
        return teams.value;
      case 'assign_agent':
        return [{ id: 'self', name: 'Self' }, ...agents.value];
      case 'add_label':
      case 'remove_label':
        return labels.value.map(i => ({
          id: i.title,
          name: i.title,
        }));
      case 'change_priority':
        return PRIORITY_CONDITION_VALUES.map(item => ({
          id: item.id,
          name: t(`MACROS.PRIORITY_TYPES.${item.i18nKey}`),
        }));
      case 'assign_issue':
        return [
          { id: 'nil', name: t('MACROS.NONE_OPTION') || 'None' },
          ...issues.value,
        ];
      case 'change_vip':
        // Use dynamic VIP levels from database if available, otherwise use defaults
        if (vipLevels.value && vipLevels.value.length > 0) {
          return [
            { id: 'none', name: t('MACROS.VIP_LEVELS.NONE') || 'None' },
            ...vipLevels.value.map(level => ({
              id: String(level.level),
              name: level.name || `VIP ${level.level}`,
            })),
          ];
        }
        // Fallback to default VIP 1-5
        return [
          { id: 'none', name: t('MACROS.VIP_LEVELS.NONE') || 'None' },
          { id: '1', name: t('MACROS.VIP_LEVELS.VIP_1') || 'VIP 1' },
          { id: '2', name: t('MACROS.VIP_LEVELS.VIP_2') || 'VIP 2' },
          { id: '3', name: t('MACROS.VIP_LEVELS.VIP_3') || 'VIP 3' },
          { id: '4', name: t('MACROS.VIP_LEVELS.VIP_4') || 'VIP 4' },
          { id: '5', name: t('MACROS.VIP_LEVELS.VIP_5') || 'VIP 5' },
        ];
      default:
        return [];
    }
  };

  return {
    getMacroDropdownValues,
  };
};
